/**
 * Shared DTOs and Zod schemas for the obligations route.
 *
 * The same Zod schemas are used by the API for input validation and
 * by the frontend's TanStack Query optimistic-update layer to shape
 * the data. Keeping them in `@almanac/shared` is the only way both
 * sides stay honest about the wire format.
 *
 * Mirrors DATA_MODEL.md §obligations.
 */

import { z } from 'zod';

export const obligationPriorities = ['low', 'medium', 'high'] as const;
export type ObligationPriority = (typeof obligationPriorities)[number];

export const obligationStatuses = ['active', 'completed', 'skipped', 'archived'] as const;
export type ObligationStatus = (typeof obligationStatuses)[number];

export const obligationVisibilities = ['private', 'shared', 'family', 'public'] as const;
export type ObligationVisibility = (typeof obligationVisibilities)[number];

export const obligationKinds = ['task', 'occasion'] as const;
export type ObligationKind = (typeof obligationKinds)[number];

// Birthday + anniversary cover the first cut; memorial / named-day can
// be added without a migration once the UI shape proves out.
export const occasionTypes = ['birthday', 'anniversary'] as const;
export type OccasionType = (typeof occasionTypes)[number];

/** ISO date `YYYY-MM-DD` or full ISO datetime — accept both. */
const isoDate = z
  .string()
  .min(8)
  .regex(/^\d{4}-\d{2}-\d{2}(T.*)?$/u, 'expected ISO date or datetime');

/** 24-hour time-of-day `HH:MM`, zero-padded. */
const isoTime = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/u, 'expected HH:MM (24-hour)');

export const reminderDaysBeforeSchema = z.array(z.number().int().min(0).max(365)).max(8);

/** Loose iCal RRULE shape — see `lib/rrule.ts` for the parser. */
const rruleString = z
  .string()
  .min(5)
  .max(500)
  .regex(/^(?:RRULE:)?FREQ=/u, 'must start with FREQ=');

const baseObligationFields = {
  id: z.string().min(8).max(64).optional(),
  title: z.string().min(1).max(200).trim(),
  description: z.string().max(2000).nullish(),
  lifeAreaId: z.string().min(1),
  dueDate: isoDate,
  // Optional time-of-day on the due date. Stored as `HH:MM`; the
  // recurrence rule continues to schedule by date only — the time
  // travels with the obligation and applies uniformly to each
  // occurrence. Always null on occasions (birthdays are all-day).
  dueTime: isoTime.nullish(),
  recurrenceRule: rruleString.nullish(),
  priority: z.enum(obligationPriorities).default('medium'),
  reminderDaysBefore: reminderDaysBeforeSchema.nullish(),
  notes: z.string().max(4000).nullish(),
  // Owner-private by default, and enforced: an obligation is visible
  // only to its creator unless shared. 'shared' opens it to the other
  // hub members (your household). See the read/write filtering in
  // routes/almanac/obligations.ts (`canAccessObligation`).
  visibility: z.enum(obligationVisibilities).default('private'),
  kind: z.enum(obligationKinds).default('task'),
  occasionType: z.enum(occasionTypes).nullish(),
  // Free-text 'what is this anniversary' (Wedding, Partnership, Met).
  // Shown only when occasionType = 'anniversary'; ignored otherwise.
  occasionLabel: z.string().max(80).nullish(),
  // The year the occasion first occurred (year of birth, year of
  // wedding). Used by the UI to compute "turns N" / "Nth anniversary".
  anchorYear: z.number().int().min(1800).max(2200).nullish(),
  // Occasion → task suggestion: the occurrence date (YYYY-MM-DD) the
  // gift-reminder prompt was dismissed for — quietly, or by creating
  // the task. Compared to the occasion's next occurrence client-side;
  // the acknowledgment bump to next year makes it stale so the prompt
  // re-arms on its own. Meaningless on tasks and simply ignored there.
  suggestionDismissedFor: isoDate.nullish(),
  personIds: z.array(z.string().min(1)).max(20).optional(),
  // The trip this task belongs to, if any. Null clears the link.
  tripId: z.string().min(1).max(64).nullish(),
} as const;

// Occasions must declare their type. Tasks must not carry an
// occasion_type or anchor_year — keeps the discriminator honest.
const occasionShapeCheck = (
  data: { kind?: ObligationKind; occasionType?: OccasionType | null; anchorYear?: number | null },
  ctx: z.RefinementCtx,
) => {
  if (data.kind === 'occasion' && !data.occasionType) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'occasionType is required when kind = "occasion"',
      path: ['occasionType'],
    });
  }
  if (data.kind === 'task' && (data.occasionType || data.anchorYear)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'occasionType and anchorYear are only valid when kind = "occasion"',
      path: ['kind'],
    });
  }
};

export const obligationCreateSchema = z.object(baseObligationFields).superRefine(occasionShapeCheck);

export const obligationUpdateSchema = z
  .object(baseObligationFields)
  .partial()
  .extend({
    status: z.enum(obligationStatuses).optional(),
    // Explicit override for the next-due date. Normally the server
    // derives `nextDueDate` from `dueDate + recurrenceRule`; this
    // field lets the client push the upcoming occurrence forward
    // (snooze) without touching the anchor `dueDate`. When supplied
    // it takes precedence over the computed value; when omitted the
    // server falls back to recomputing as before.
    nextDueDate: isoDate.optional(),
    // `baseObligationFields` bakes in create-time defaults for these
    // three. Zod's `.partial()` does NOT strip a `.default()` — an
    // omitted field still parses to its default — so on a partial
    // update the default would clobber the stored value (e.g. clearing
    // a due time would parse to `{ dueTime: null, visibility: 'private',
    // priority: 'medium', kind: 'task' }` and silently un-share the
    // item, drop its priority, and break an occasion). Override as
    // plain optionals so "omitted" means "leave unchanged".
    priority: z.enum(obligationPriorities).optional(),
    visibility: z.enum(obligationVisibilities).optional(),
    kind: z.enum(obligationKinds).optional(),
  })
  .superRefine(occasionShapeCheck);

export const obligationCompleteSchema = z.object({
  completedAt: isoDate.optional(),
});

export const obligationReopenSchema = z.object({
  // Optional snapshot of the pre-completion `nextDueDate`. Lets the
  // client restore the exact prior value when undoing the completion of
  // a recurring obligation; without it the server recomputes from the
  // rule, which can land on a different occurrence depending on `now`.
  nextDueDate: isoDate.optional(),
});

export type ObligationCreateInput = z.infer<typeof obligationCreateSchema>;
export type ObligationUpdateInput = z.infer<typeof obligationUpdateSchema>;
export type ObligationCompleteInput = z.infer<typeof obligationCompleteSchema>;
export type ObligationReopenInput = z.infer<typeof obligationReopenSchema>;

/**
 * Wire shape returned by the API. `reminderDaysBefore` is stored as a
 * JSON-encoded array in D1 but exposed as a real array on the wire.
 */
export interface ObligationDTO {
  id: string;
  title: string;
  description: string | null;
  lifeAreaId: string;
  dueDate: string;
  /** Optional time-of-day on the due date, `HH:MM` 24-hour. Null
   *  for all-day items. Always null on occasions. */
  dueTime: string | null;
  nextDueDate: string | null;
  recurrenceRule: string | null;
  priority: ObligationPriority;
  status: ObligationStatus;
  reminderDaysBefore: number[] | null;
  notes: string | null;
  visibility: ObligationVisibility;
  kind: ObligationKind;
  occasionType: OccasionType | null;
  occasionLabel: string | null;
  anchorYear: number | null;
  suggestionDismissedFor: string | null;
  personIds: string[];
  tripId: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface LifeAreaDTO {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  sortOrder: number;
  visibility: ObligationVisibility;
}
